



// Vue
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Mixins } from 'vue-mixin-decorator';
import { mapGetters, mapActions } from 'vuex';

// Third Party
import dayjs, { UnitType } from 'dayjs';
import 'dayjs/locale/es';

// Constants
import Assets from '@/constants/assets.ts';

// Components
import TableColumnOrder from '@/shared/components/TableColumnOrder.component.vue';
import { FlatSelectorTextBorderBottom } from '@/shared/components/FlatSelectorText.component.vue';
import Btn, { BtnColor, BtnWidth } from '@/shared/components/btn.component.vue';
import ConvocationRow, {
    ParticipantListType,
} from '@/manager/convocation/views/convocationDetail/convocationRow/convocationRow.view.vue';
import DropUp, { DropUpColorHover } from '@/shared/components/DropUp.component.vue';
import GroupFilter from '@/shared/components/GroupFilter.component.vue';
import Icon, { IconEnum } from '@/shared/components/icon.component.vue';
import Pagination from '@/shared/components/Pagination/Pagination.component.vue';
import ItemThumb from '@/shared/components/item-thumb.component.vue';
import Spinner from '@/shared/components/Spinner.component.vue';
import EmptyState from '@/shared/components/empty-state.component.vue';

// Models - Interfaces
import { SecondLevelOptionModel } from '@/shared/models/secondLevelOption.model';
import { Convocation } from '@/manager/convocation/convocation.model';
import { CounterColor } from '@/shared/components/counter/counter.enum.ts';
import { CounterInterface } from '@/shared/components/counter/counter.interface.ts';
import { TimetablesInterface } from '@/shared/interfaces/timetables.interface';
import { Participant } from '@/manager/convocation/participant.model';

// Services
import { ConvocationService } from '@/manager/convocation/convocation.service';

// Mixins
import WithSpinner from '@/shared/mixins/withSpinner.mixin.ts';
import WithPagination from '@/shared/mixins/withPagination.mixin.ts';
import ColumnOrderMixin from '@/shared/mixins/columnOrder.mixin.ts';
import SearchMixin from '@/shared/mixins/search.mixin.ts';

export enum ListActions {
    DownloadCsv = 'download-csv',
    EmailToMultiple = 'email-to-multiple',
}

export interface Filters {
    summoned: boolean;
    confirmed: boolean;
    not_confirmed: boolean;
    out: boolean;
    postpone: boolean;
    checked: boolean;
}

interface MixinInterface extends WithPagination, SearchMixin, ColumnOrderMixin {}

@Component({
    components: {
        Btn,
        ConvocationRow,
        DropUp,
        EmptyState,
        GroupFilter,
        Icon,
        ItemThumb,
        Pagination,
        Spinner,
        TableColumnOrder,
    },
    computed: {
        ...mapGetters({
            getConvocation: 'getConvocation',
            getTeamList: 'getTeamList',
            isOpenNavbar: 'isOpenNavbar',
            participantMassiveEmailList: 'getParticipantMassiveEmailList',
        }),
    },
})
export default class TeamList extends Mixins<MixinInterface> (
    WithPagination,
    SearchMixin,
    ColumnOrderMixin,
) {
    @Prop() private convocationId!: string;
    @Prop() private isLive!: boolean;
    @Prop() private parsedNextConvocationListOptions!: any[];
    @Prop() private initialNextConvocationListIndex!: number;
    @Prop() private paymentStatusOptions!: any[];
    @Prop() private columnsVisibility!: any;
    @Prop() private searchValue!: string;
    @Prop() private searchNifValue!: string;
    @Prop() private headerClass!: string;

    private assets = Assets;
    private dropUpColorHover = DropUpColorHover;
    private iconEnum = IconEnum;
    private btnColor = BtnColor;
    private btnWidth = BtnWidth;
    private getConvocation!: any;
    private participantMassiveEmailList!: any;
    private loadingGroupFilters = true;
    private getTeamList!: Participant[];
    private isOpenNavbar!: any;
    private lastNoteTyped: string = '';
    private interval!: any;
    private cleanSearch = false;
    private teamListLoaded = false;
    private participantListType = ParticipantListType;
    private flatSelectorTextBorderBottom = FlatSelectorTextBorderBottom;


    @Watch('searchValue')
    public watchSearchValue(newVal: any, oldVal: any) {
        this.searchString = this.searchValue;
    }

    @Watch('searchNifValue')
    public watchSearchNifValue(newVal: any, oldVal: any) {
        this.searchNifString = this.searchNifValue;
    }

    get convocation() {
        return this.getConvocation(this.convocationId);
    }

    get showPagination() {
        return this.teamListLoaded && !this.spinner.show && this.getTeamList.length > 0;
    }

    get showTable() {
        const result = (
            this.teamListLoaded &&
            !this.spinner.show &&
            !this.showEmptyState
        );
        this.$emit('show-table', result);
        return result;
    }

    get lastColumnTitle() {
        return (this.isLive) ? 'Ent/Reg/Dip' : 'Ent/Dip';
    }

    get lastColumnClass() {
        return (this.isLive) ? 'table-cell--last-icons-convocation' : 'table-cell--last-icons-convocation--future';
    }

    get dropUpOptions() {
        return [
            { value: ListActions.DownloadCsv, name: 'Exportar lista' },
            { value: ListActions.EmailToMultiple, name: 'Email masivo' },
        ];
    }

    get isFirstDay(): boolean {
        let result = false;
        const now = dayjs();
        const timetables = this.convocation.timetables;
        if (timetables) {
            const firstDay = dayjs(timetables[0].date, { locale: 'es' });
            if ( now.diff(firstDay, ( 'days' as UnitType )) === 0 ) {
                result = true;
            }
        }
        return result;
    }

    get showEmptyState() {
        return (
            this.teamListLoaded &&
            this.getTeamList.length === 0 &&
            !this.spinner.show
        );
    }


    public fetchList() {
        this.requestShowSpinner();
        this.fetchParticipantList((res: any) => {
            this.onFetchSuccess(res);
            this.$emit('list-loaded', res);
            this.teamListLoaded = true;
            this.changeSpinnerVisibility(false);
        });
    }

    public searchCallback() {
        this.fetchFirstPageList();
    }

    private created() {
        this.changeSpinnerVisibility(true);
        this.fetchFirstPageList();
        this.searchNifString = this.searchNifValue;
    }

    private refetchList() {
        this.$emit('refetch-list');
    }

    private formatConvocationDates( convocation: Convocation ): string {
        return ConvocationService.formatDates(
            convocation.timetables as TimetablesInterface[],
        );
    }

    // tslint:disable-next-line:member-ordering
    public columnOrderFetchList() {
        this.requestShowSpinner();
        this.fetchParticipantList(
            (res: any) => {
                this.columnOrderOnFetchSuccess();
                this.changeSpinnerVisibility( false );
            },
        );
    }

    private fetchParticipantChecked() {
        this.$store.dispatch(
            'fetchParticipantChecked',
            { convocationId: this.convocationId },
        );
    }

    private fetchParticipantList( callback: any = () => { const doNothing = 1; } ) {
        this.$store.dispatch(
            'fetchTeamList',
            {
                id: this.convocationId,
                offset: this.offset,
                limit: this.limit,
                ordering: this.composeOrdering(),
                search: this.searchString,
                searchNif: this.searchNifString,
            },
        ).then((res: any) => {
            this.changeSpinnerVisibility( false );
            if ( callback ) {
                callback(res);
            }
        })
        .catch((err) => {
            this.changeSpinnerVisibility( false );
        });
    }

    private fetchTagList() {
        this.$store.dispatch( 'fetchTagList' );
    }

    private fetchNextConvocationList( convocationId: string ) {
        this.$store.dispatch( 'fetchNextConvocationList', {
            id: convocationId,
        } );
    }

    private fetchDelegationList( callback: any = () => { const doNothing = 1; } ) {
        this.$store.dispatch( 'fetchDelegationList' ).then((res: any) => {
            callback();
        });
    }

    private fetchParticipantMassiveEmailList(callback: any = () => { const doNothing = 1; } ) {
        const params = {
            search: this.searchString,
            team_list: true,
            waiting_list: false,
            waiting_repeater_list: false,
        };
        this.$store.dispatch('fetchParticipantMassiveEmail', {
            convocationId: this.convocationId,
            params,
        }).then(
            (res) => {
                callback();
            },
        );
    }

    private dropUpOptionSelected(option: any) {
        switch (option.value) {
            case ListActions.DownloadCsv:
                ConvocationService.downloadCsv(this.convocationId, { team_list: true });
                break;
            case ListActions.EmailToMultiple:
                this.fetchParticipantMassiveEmailList(
                    () => {
                        ConvocationService.mailToFilteredUsers(this.participantMassiveEmailList);
                    },
                );
                break;
        }
    }


}
